/**
 * "Hay una version nueva", en el marco. La logica de cuando sale vive en
 * `lib/app-update.ts`; aqui solo se pinta.
 *
 * Dos formas, una por marco: tarjeta al pie de la barra lateral (escritorio y
 * all-in-one) y franja bajo el encabezado (telefono y tablet). Ninguna flota
 * —nada del marco usa `position: fixed` (§9)— y ninguna tapa nada: el
 * encabezado publica su alto medido (`usePublishedHeight`), asi que la franja
 * empuja el contenido en vez de taparle el primer renglon.
 *
 * "Ahora no" es una cruz y no un boton con letrero porque no cabe: la barra
 * lateral deja 190 px por dentro de la tarjeta y un telefono de 360 px pierde
 * la frase entera con dos botones con texto. La cruz la quita hasta que llegue
 * una version mas nueva que esta; recargar por cualquier otro motivo tambien la
 * estrena, porque la navegacion trae la pagina nueva de la red.
 */

import type { ReactNode } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { applyUpdate, dismissUpdate, useUpdateStatus } from '../lib/app-update.js';
import { Button } from './ui.js';

const TITLE = 'Hay una versión nueva';

export function UpdateNotice({ variant }: { variant: 'sidebar' | 'header' }): ReactNode {
  const status = useUpdateStatus();
  if (status === 'idle') return null;
  const applying = status === 'applying';

  const later = (
    <Button
      size="sm"
      variant="ghost"
      onClick={dismissUpdate}
      disabled={applying}
      title="Ahora no"
      aria-label="Ahora no"
      className="w-9 shrink-0 px-0"
    >
      <X className="size-4" aria-hidden />
    </Button>
  );
  const update = (
    <Button size="sm" variant="primary" onClick={applyUpdate} loading={applying} block={variant === 'sidebar'}>
      Actualizar
    </Button>
  );

  if (variant === 'header') {
    return (
      <div role="status" className="border-t border-line px-4 py-2 flex items-center gap-2">
        <RefreshCw className="size-4 shrink-0 text-primary" aria-hidden />
        <p className="flex-1 min-w-0 truncate text-[13px] font-medium">{TITLE}</p>
        {update}
        {later}
      </div>
    );
  }

  return (
    <div role="status" className="rounded-xl border border-primary/30 bg-primary-soft p-3">
      <div className="flex items-start gap-2">
        <RefreshCw className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold text-primary">{TITLE}</p>
          {/* Es la pregunta que detiene el toque en el mostrador: el ticket vive
              en un borrador (`lib/draft.tsx`) y sobrevive a la recarga. */}
          <p className="mt-0.5 text-[12px] text-muted">Toma un segundo y el ticket que estés armando no se pierde.</p>
        </div>
        <div className="-mt-1.5 -mr-1.5">{later}</div>
      </div>
      <div className="mt-2.5">{update}</div>
    </div>
  );
}
